import React, { CSSProperties, FC, MouseEvent, useCallback } from 'react';
import SpreadsheetRowLink from './SpreadsheetRowLink';
import classNames from 'classnames';
import classes from './styles/index.module.scss';
import { ErrorBoundary } from '../../../../../../ReactSharedScripts/components/ErrorBoundary';
import { allowNavEvent } from '../../../../../../ReactSharedScripts/components/RouteLink/utils';

type SpreadsheetBodyRowProps = {
	id?: string | number;
	url?: string;
	onClick?: (id: string | number | undefined, e: MouseEvent) => void;
	disabled?: boolean;
	selected?: boolean;
	hasHover?: boolean;
	hasAvatar?: boolean;
	className?: string;
	style?: CSSProperties;
	dataTestId?: string;
	hasCheckbox?: boolean;
	alignCenter?: boolean;
	cursorPointer?: boolean;
	preventNavigate?: boolean;
	forceUseLink?: boolean;
};

export const SpreadsheetBodyRow: FC<React.PropsWithChildren<SpreadsheetBodyRowProps>> = ({
	id,
	url,
	onClick,
	disabled = false,
	children,
	selected = false,
	hasHover = true,
	hasAvatar = false,
	className,
	dataTestId = 'spreadsheetBodyRow',
	hasCheckbox = false,
	alignCenter,
	cursorPointer = true,
	preventNavigate = false,
	forceUseLink,
	style,
}) => {
	const hasUrl = typeof url !== 'undefined';
	const hasOnClickHandler = typeof onClick !== 'undefined';

	const isClickable = !disabled && (hasUrl || hasOnClickHandler);

	const componentClasses = classNames(classes.bodyRow, className, {
		[classes.bodyRow_hasHover]: hasHover,
		[classes.bodyRow_selected]: selected,
		[classes.bodyRow_hasCursorPointer]: cursorPointer && isClickable,
		[classes.row_hasAvatar]: hasAvatar,
		[classes.bodyRow_hasCheckbox]: hasCheckbox,
		[classes.bodyRow_disabled]: disabled,
		[classes.bodyRow_alignCenter]: alignCenter,
	});

	const handleBodyRowClick = useCallback(
		(e) => {
			if (!allowNavEvent(e) || disabled || typeof onClick === 'undefined') {
				return;
			}

			onClick(id, e);
		},
		[disabled, id, onClick],
	);

	if (!hasUrl || preventNavigate) {
		return (
			<li
				className={componentClasses}
				onClick={handleBodyRowClick}
				data-test-id={dataTestId}
			>
				{children}
			</li>
		);
	}

	return (
		<ErrorBoundary>
			<SpreadsheetRowLink
				href={url}
				className={componentClasses}
				tagName='li'
				style={style}
				forceUseLink={forceUseLink}
			>
				{children}
			</SpreadsheetRowLink>
		</ErrorBoundary>
	);
};
